import { formatCurrencyForDisplay, resolveTransferTimeLabel } from '@utils'
import type { PaymentOptionResponse } from 'mmfintech-backend-api/types'
import { formatMoney, tr } from 'mmfintech-commons'

import styles from './SendMoneyMethodInfo.module.scss'

interface SendMoneyMethodInfoProps {
  paymentOption: PaymentOptionResponse
  currencyCode?: string
}

function formatPercent(value: number | undefined, emptyText: string): string {
  return typeof value === 'number' ? `${value}%` : emptyText
}

function formatFee(amount: number | undefined, currency: string | undefined, emptyText: string): string {
  return amount && amount > 0 ? formatMoney(amount, formatCurrencyForDisplay(currency)) : emptyText
}

export function SendMoneyMethodInfo({ paymentOption, currencyCode }: SendMoneyMethodInfoProps) {
  const { feePercent, feeFlat, feeFlatCurrency, minAmount, singlePaymentMaxAmount } = paymentOption

  // Generic labels are shared with the deposit info card (same admin-managed copy) to avoid duplicate keys.
  const noneText = tr('FRONTEND.DEPOSIT.INFO.NONE', 'None')

  // Details with an empty value are skipped (only transfer time can be empty).
  const details = [
    {
      key: 'fee-percent',
      label: tr('FRONTEND.DEPOSIT.INFO.FEE_PERCENT', 'Fee (%)'),
      value: formatPercent(feePercent, noneText)
    },
    {
      key: 'flat-fee',
      label: tr('FRONTEND.DEPOSIT.INFO.FLAT_FEE', 'Flat fee'),
      value: formatFee(feeFlat, feeFlatCurrency, noneText)
    },
    {
      key: 'transfer-time',
      label: tr('FRONTEND.DEPOSIT.INFO.TRANSFER_TIME', 'Transfer time'),
      value: resolveTransferTimeLabel(paymentOption)
    },
    {
      key: 'min-amount',
      label: tr('FRONTEND.SEND_MONEY.INFO.MIN_AMOUNT', 'Minimum amount'),
      value: formatFee(minAmount, currencyCode, tr('FRONTEND.SEND_MONEY.INFO.NO_MIN_AMOUNT', 'No minimum amount'))
    },
    {
      key: 'max-amount',
      label: tr('FRONTEND.SEND_MONEY.INFO.MAX_AMOUNT', 'Maximum single payment amount'),
      value: formatFee(
        singlePaymentMaxAmount,
        currencyCode,
        tr('FRONTEND.SEND_MONEY.INFO.NO_MAX_AMOUNT', 'No max amount')
      )
    }
  ]

  return (
    <div className={styles['send-money-method-info']} data-test='send-money-method-info'>
      {details
        .filter(detail => detail.value)
        .map(detail => (
          <div className={styles['info-row']} key={detail.key}>
            <span className={styles['info-label']}>{detail.label}</span>
            <span className={styles['info-value']}>{detail.value}</span>
          </div>
        ))}

      <div className={styles['info-disclaimer']}>
        {tr(
          'FRONTEND.DEPOSIT.INFO.DISCLAIMER',
          'Exact processing times may vary due to bank processing schedules, security and verification checks, public holidays, and other operational factors.'
        )}
      </div>
    </div>
  )
}
